<template>
    <form class="flex items-center justify-between gap-7 relative z-10">
        <input
            type="hidden"
            name="s"
            value=""
        >

        <div
            class="w-full flex 2xl:flex-wrap 3xl:flex-nowrap justify-between 2xl:justify-normal items-center sm:gap-x-7 gap-y-3.5">
            <TypeToggle
                v-model:type="type"
                @change="loadResults"
                class="shrink-0"
            />

            <div class="flex gap-2 xl:gap-5 3xl:gap-7 2xl:justify-between items-center 2xl:w-full">
                <div class="2xl:w-full max-w-[1360px] flex gap-5 3xl:gap-7">
                    <template v-if="isDesktop">
                        <div class="w-full flex flex-wrap lg:flex-nowrap gap-2.5 lg:gap-0">
                            <RegionsSelector
                                class="w-full lg:w-1/3 lg:pr-[5px]"
                                v-model:loading="loading"
                                v-model:selected="regionsSelected"
                                @selected="regionsSelected = $event"
                                @close="loadResults()"
                            />

                            <DatesSelector
                                class="w-full lg:w-1/3"
                                v-model:loading="loading"
                                v-model:startDate="startDate"
                                v-model:endDate="endDate"
                                @datesSelected="startDate = $event.startDate; endDate = $event.endDate; cacheDates($event.startDate, $event.endDate); loadResults();"
                            />

                            <GuestsSelector
                                v-if="type == 'stays'"
                                class="w-full lg:w-1/3 lg:pl-[5px]"
                                v-model:loading="loading"
                                v-model:guestsCount="guestsCount"
                                v-model:bedroomsCount="bedroomsCount"
                                v-model:bathroomsCount="bathroomsCount"
                                @change="guestsCount = $event.guests; bedroomsCount = $event.bedrooms; bathroomsCount = $event.bathrooms;"
                                @close="loadResults()"
                            />

                            <CapacitySelector
                                v-else-if="type == 'weddings'"
                                class="w-full lg:w-1/3 lg:pl-[5px]"
                                v-model:loading="loading"
                                v-model:capacityCount="capacityCount"
                                v-model:seatedIndoorsCount="seatedIndoorsCount"
                                @change="capacityCount = $event.capacity; seatedIndoorsCount = $event.seated_indoors;"
                                @close="loadResults()"
                            />
                        </div>
                    </template>

                    <div class="flex items-center gap-2 xl:gap-5 3xl:gap-7">
                        <FiltersSelector
                            v-model:selectedCount="selectedFiltersCount"
                            v-model:loading="loading"
                            v-model:resultsCount="resultsCount"
                            @clear="clearSelected"
                            @close="loadResults()"
                        >
                            <template #modal>
                                <div class="border-t border-brand-2-950/10 pt-6 sm:pt-10">
                                    <div
                                        v-if="!isDesktop"
                                        class="mb-6 sm:mb-10 pb-6 sm:pb-10 border-b border-brand-2-950/10"
                                    >
                                        <div class="w-full space-y-1.5 lg:space-y-2.5">
                                            <RegionsSelector
                                                v-model:loading="loading"
                                                v-model:selected="regionsSelected"
                                                @selected="regionsSelected = $event;"
                                            />

                                            <DatesSelector
                                                v-model:loading="loading"
                                                v-model:startDate="startDate"
                                                v-model:endDate="endDate"
                                                @datesSelected="startDate = $event.startDate; endDate = $event.endDate;"
                                            />

                                            <GuestsSelector
                                                v-if="type == 'stays'"
                                                v-model:loading="loading"
                                                v-model:guestsCount="guestsCount"
                                                v-model:bedroomsCount="bedroomsCount"
                                                v-model:bathroomsCount="bathroomsCount"
                                                @change="guestsCount = $event.guests; bedroomsCount = $event.bedrooms; bathroomsCount = $event.bathrooms;"
                                                class="w-full"
                                            />

                                            <CapacitySelector
                                                v-else-if="type == 'weddings'"
                                                v-model:loading="loading"
                                                v-model:capacityCount="capacityCount"
                                                v-model:seatedIndoorsCount="seatedIndoorsCount"
                                                @change="capacityCount = $event.capacity; seatedIndoorsCount = $event.seated_indoors;"
                                                class="w-full"
                                            />
                                        </div>
                                    </div>

                                    <div class="mb-6 sm:mb-10 pb-6 sm:pb-10 border-b border-brand-2-950/10">
                                        <PriceSelector
                                            v-model:loading="loading"
                                            @pricesChanged="prices = $event"
                                        />
                                    </div>

                                    <div class="mb-6 sm:mb-10 pb-6 sm:pb-10 border-b border-brand-2-950/10">
                                        <FeaturesSelector
                                            v-if="type == 'stays'"
                                            v-model:loading="loading"
                                            v-model:type="type"
                                            v-model:selected="featuresSelected"
                                            @selected="featuresSelected = $event"
                                        />

                                        <FeaturesSelector
                                            v-else-if="type == 'weddings'"
                                            v-model:loading="loading"
                                            v-model:type="type"
                                            v-model:selected="weddingFeaturesSelected"
                                            @selected="weddingFeaturesSelected = $event"
                                        />
                                    </div>

                                    <div class="mb-6 sm:mb-10 pb-6 sm:pb-10 border-b border-brand-2-950/10">
                                        <AccessibilitySelector
                                            v-model:loading="loading"
                                            v-model:selected="accessibilitySelected"
                                            @selected="accessibilitySelected = $event"
                                        />
                                    </div>

                                    <HouseNameSelector
                                        v-model:loading="loading"
                                        v-model:type="type"
                                        v-model:value="houseNameSelected"
                                        @value="houseNameSelected = $event"
                                        @selected="weddingFeaturesSelected = $event"
                                    />
                                </div>
                            </template>
                        </FiltersSelector>

                        <SortSelector
                            v-model:type="type"
                            v-model:selected="sort"
                            @change="sort = $event; loadResults()"
                        />
                    </div>
                </div>

                <div class="shrink-0 hidden sm:block">
                    <a
                        v-if="filtersSelected"
                        :href="urls.properties"
                        class="button button-ghost button-small"
                    >
                        Clear All
                    </a>
                </div>
            </div>
        </div>
    </form>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue';

    import axios from 'axios';

    import { debounce } from '@js/src/helpers';
    import googleMaps from '@js/src/googleMaps';
    import { hydrateFavourites } from '@js/src/favourites';
    import { sliders } from '@js/src/integrations/flickity';

    import TypeToggle from '@js/src/components/TypeToggle.vue';
    import RegionsSelector from '@js/src/components/RegionsSelector.vue';
    import DatesSelector from '@js/src/components/DatesSelector.vue';
    import GuestsSelector from '@js/src/components/GuestsSelector.vue';
    import CapacitySelector from '@js/src/components/CapacitySelector.vue';
    import FiltersSelector from '@js/src/components/FiltersSelector.vue';
    import PriceSelector from '@js/src/components/PriceSelector.vue';
    import FeaturesSelector from '@js/src/components/FeaturesSelector.vue';
    import AccessibilitySelector from '@js/src/components/AccessibilitySelector.vue';
    import HouseNameSelector from '@js/src/components/HouseNameSelector.vue';
    import SortSelector from '@js/src/components/SortSelector.vue';

    import { cacheDates } from '@js/src/helpers';

    const resultsCount = ref(0);

    const urls = ref(Filters.urls);
    const type = ref(Filters.type);

    const loading = ref(false);

    const isDesktop = ref(true);
    const mediaQuery = ref(window.matchMedia("(max-width: 1280px)"));

    const regionsSelected = ref(Filters.regionsSelected);
    const featuresSelected = ref(Filters.featuresSelected);
    const accessibilitySelected = ref(Filters.accessibilitySelected);
    const weddingFeaturesSelected = ref(Filters.weddingFeaturesSelected);
    const houseNameSelected = ref(Filters.houseNameSelected);
    const startDate = ref(Filters.startDate);
    const endDate = ref(Filters.endDate);
    const guestsCount = ref(Filters.guestsCount);
    const bedroomsCount = ref(Filters.bedroomsCount);
    const bathroomsCount = ref(Filters.bathroomsCount);
    const capacityCount = ref(Filters.capacityCount);
    const seatedIndoorsCount = ref(Filters.seatedIndoorsCount);
    const prices = ref([Filters.priceMinCurrent, Filters.priceMaxCurrent]);
    const view = ref(Filters.view);
    const sort = ref(Filters.sort);

    const filtersSelected = computed(() => {
        return regionsSelected.value.length > 0 ||
            featuresSelected.value.length > 0 ||
            weddingFeaturesSelected.value.length > 0 ||
            (startDate.value && endDate.value) ||
            guestsCount.value > 0 ||
            bedroomsCount.value > 0 ||
            bathroomsCount.value > 0 ||
            capacityCount.value > 0 ||
            seatedIndoorsCount.value > 0 ||
            prices.value[0] ||
            prices.value[1] ||
            houseNameSelected.value;
    });

    const selectedFiltersCount = computed(() => {
        return type.value == 'weddings' ? weddingFeaturesSelected.value.length : featuresSelected.value.length + accessibilitySelected.value.length;
    });

    onMounted(() => {
        isDesktop.value = !mediaQuery.value.matches;

        window.addEventListener('resize', debounce(() => {
            mediaQuery.value = window.matchMedia("(max-width: 1280px)");
            isDesktop.value = !mediaQuery.value.matches;
        }, 100));
    });

    watch(() => type.value, () => {
        addTypeToUrl();
    });

    function clearSelected() {
        featuresSelected.value = [];
        weddingFeaturesSelected.value = [];

        loadResults();
    }

    function loadResults() {
        if (type.value != 'stays' && (sort.value == 'guests_high' || sort.value == 'guests_low' || sort.value == 'bedrooms_high' || sort.value == 'bedrooms_low')) {
            sort.value = 'relevance';
        }

        if (type.value != 'weddings' && (sort.value == 'capacity_high' || sort.value == 'capacity_low' || sort.value == 'seating_high' || sort.value == 'seating_low')) {
            sort.value = 'relevance';
        }

        loading.value = true;

        const noResultsElement = document.querySelector('[data-no-results]');
        const countElement = document.querySelector('[data-ajax-results-count="properties"]');
        const htmlWrapper = document.querySelector('[data-ajax-results="properties"]');
        const htmlElement = htmlWrapper ? htmlWrapper.querySelector('[data-properties]') : '';
        const mapWrapper = document.querySelector('[data-ajax-results-map="properties"]');
        const mapElement = mapWrapper ? mapWrapper.querySelector('.google-map') : '';
        const paginationElement = document.querySelector('[data-ajax-results-pagination="properties"]');

        if (htmlElement) {
            htmlElement.classList.add('opacity-60', 'pointer-events-none');
        }

        axios.get('/wp-json/lh/v1/properties', {
            params: {
                user_id: Page.userId,
                type: type.value,
                regions: regionsSelected.value,
                features: featuresSelected.value,
                wedding_features: weddingFeaturesSelected.value,
                accessibility: accessibilitySelected.value,
                start_date: startDate.value,
                end_date: endDate.value,
                guests: guestsCount.value,
                bedrooms: bedroomsCount.value,
                bathrooms: bathroomsCount.value,
                capacity: capacityCount.value,
                seated_indoors: seatedIndoorsCount.value,
                price_min: prices.value[0],
                price_max: prices.value[1],
                house: houseNameSelected.value,
                sort: sort.value,
                view: view.value,
                page: 1,
            },
        },
        )
            .then(response => {
                resultsCount.value = response.data.count;

                if (response.data.count === 0) {
                    noResultsElement ? noResultsElement.classList.remove('hidden') : '';
                    htmlWrapper ? htmlWrapper.classList.add('hidden') : '';
                    htmlElement ? htmlElement.classList.add('hidden') : '';
                    mapWrapper ? mapWrapper.classList.add('hidden') : '';
                    mapElement ? mapElement.classList.add('hidden') : '';
                    paginationElement ? paginationElement.classList.add('hidden') : '';
                } else {
                    noResultsElement ? noResultsElement.classList.add('hidden') : '';
                    htmlWrapper ? htmlWrapper.classList.remove('hidden') : '';
                    htmlElement ? htmlElement.classList.remove('hidden') : '';
                    mapWrapper ? mapWrapper.classList.remove('hidden') : '';
                    mapElement ? mapElement.classList.remove('hidden') : '';
                    paginationElement ? paginationElement.classList.remove('hidden') : '';
                }

                if (response.data.pages < 2) {
                    paginationElement ? paginationElement.classList.add('hidden') : '';
                } else {
                    paginationElement ? paginationElement.classList.remove('hidden') : '';
                }

                if (countElement) {
                    countElement.innerHTML = response.data.countText;
                }

                if (htmlElement) {
                    htmlElement.innerHTML = response.data.html;
                }

                if (mapElement) {
                    mapElement.classList.remove('loaded');
                    mapElement.innerHTML = response.data.mapHtml;

                    setTimeout(() => {
                        googleMaps();
                    }, 10);
                }

                if (paginationElement) {
                    paginationElement.innerHTML = response.data.paginationHtml;
                }

                addSelectedToUrl();

                setTimeout(() => {
                    sliders();
                    hydrateFavourites();
                }, 10);
            })
            .catch(error => {
                resultsCount.value = 0;

                const responseErrors = error?.response?.data?.data?.errors || {};
                const firstError = Object.values(responseErrors)[0] || '';

                alert(
                    Object.values(firstError)[0] ?
                        Object.values(firstError)[0] :
                        (error?.response?.data?.message || 'Sorry, an error occurred.'));
            })
            .finally(() => {
                loading.value = false;

                if (htmlElement) {
                    htmlElement.classList.remove('opacity-60', 'pointer-events-none');
                }
            });
    }

    function addTypeToUrl() {
        const url = new URL(window.location.href);

        url.searchParams.delete('type');

        if (type.value != 'stays') {
            url.searchParams.append('type', type.value);
        }

        window.history.pushState({}, '', url);
    }

    function addSelectedToUrl() {
        const url = new URL(window.location.href);

        url.searchParams.delete('s');
        url.searchParams.delete('regions[]');
        url.searchParams.delete('features[]');
        url.searchParams.delete('wedding_features[]');
        url.searchParams.delete('accessibility[]');
        url.searchParams.delete('start_date');
        url.searchParams.delete('end_date');
        url.searchParams.delete('guests');
        url.searchParams.delete('bedrooms');
        url.searchParams.delete('bathrooms');
        url.searchParams.delete('capacity');
        url.searchParams.delete('seated_indoors');
        url.searchParams.delete('price_min');
        url.searchParams.delete('price_max');
        url.searchParams.delete('house');
        url.searchParams.delete('view');
        url.searchParams.delete('sort');
        url.searchParams.delete('page');

        url.searchParams.append('s', '');

        regionsSelected.value.forEach(region => {
            url.searchParams.append('regions[]', region);
        });

        if (type.value == 'stays') {
            featuresSelected.value.forEach(feature => {
                url.searchParams.append('features[]', feature);
            });
        } else if (type.value == 'weddings') {
            weddingFeaturesSelected.value.forEach(feature => {
                url.searchParams.append('wedding_features[]', feature);
            });
        }

        accessibilitySelected.value.forEach(accessibility => {
            url.searchParams.append('accessibility[]', accessibility);
        });

        if (startDate.value) {
            url.searchParams.append('start_date', startDate.value);
        }

        if (endDate.value) {
            url.searchParams.append('end_date', endDate.value);
        }

        if (type.value == 'stays') {
            if (guestsCount.value > 0) {
                url.searchParams.append('guests', guestsCount.value);
            }

            if (bedroomsCount.value > 0) {
                url.searchParams.append('bedrooms', bedroomsCount.value);
            }

            if (bathroomsCount.value > 0) {
                url.searchParams.append('bathrooms', bathroomsCount.value);
            }
        } else if (type.value == 'weddings') {
            if (capacityCount.value > 0) {
                url.searchParams.append('capacity', capacityCount.value);
            }

            if (seatedIndoorsCount.value > 0) {
                url.searchParams.append('seated_indoors', seatedIndoorsCount.value);
            }
        }

        if (prices.value[0]) {
            url.searchParams.append('price_min', prices.value[0]);
        }

        if (prices.value[1]) {
            url.searchParams.append('price_max', prices.value[1]);
        }

        if (houseNameSelected.value) {
            url.searchParams.append('house', houseNameSelected.value);
        }

        if (view.value) {
            url.searchParams.append('view', view.value);
        }

        if (sort.value) {
            url.searchParams.append('sort', sort.value);
        }

        window.history.pushState({}, '', url);

        updateViewLink('grid', url.href);
        updateViewLink('map', url.href)
    }

    function updateViewLink(type, url) {
        const linkElement = document.querySelector(`[data-results-${type}-link]`);

        if (linkElement) {
            let linkUrl = new URL(url);

            linkUrl.searchParams.delete('view');

            if (type == 'map') {
                linkUrl.searchParams.append('view', 'map');
            }

            linkElement.href = linkUrl.href;
        }
    }
</script>
